import type { GrammarRule } from './grammar'

/**
 * Mandarin grammar, from HSK 1 to HSK 6.
 *
 * The rules an English speaker actually needs, in the order they start to
 * matter: word order first, because it is where English instincts go wrong
 * most often and most audibly, then the particles, then the constructions that
 * have no English counterpart at all. Levels follow what levelGuidance() tells
 * the generator, so the grammar a rule introduces is the grammar the islands at
 * that level are allowed to use — 把 and 被 first appear at HSK 3 in both.
 *
 * Pinyin is written as spoken, one syllable per character, with 一 and 不
 * sandhi applied, exactly like the islands. grammar.test.ts checks the syllable
 * count against the characters and the 一/不 tones against the syllable after
 * them, and grades every example against itself.
 */
export const GRAMMAR_ZH: GrammarRule[] = [
  // ------------------------------------------------------------------ HSK 1
  {
    id: 'zh-verbs-never-change',
    level: 1,
    topic: 'verbs',
    title: 'Verbs never change form',
    rule: 'A Chinese verb is the same for I, you and they, and for yesterday, today and tomorrow. Time words do the work that verb endings do in English.',
    pattern: ['Subject', 'Time', 'Verb'],
    examples: [
      { text: '[我](Subject)[今天](Time)[去](Verb)。', pinyin: 'wǒ jīn tiān qù', english: 'I’m going today.' },
      { text: '[他们](Subject)[明天](Time)[去](Verb)。', pinyin: 'tā men míng tiān qù', english: 'They’re going tomorrow.' },
      { text: '[她](Subject)[每天](Time)[去](Verb)。', pinyin: 'tā měi tiān qù', english: 'She goes every day.' },
    ],
    note: 'For something already finished, 了 usually joins in — see the rule on 了 after a verb.',
  },
  {
    id: 'zh-time-manner-place',
    level: 1,
    topic: 'order',
    title: 'When, how, where — then the verb',
    rule: 'Everything that sets the scene goes before the verb. Time comes first; how, or who with, usually comes next; where comes right before the verb.',
    pattern: ['Subject', 'Time', 'Manner', 'Place', 'Verb', 'Object'],
    examples: [
      {
        text: '[我](Subject)[明天](Time)[在家](Place)[工作](Verb)。',
        pinyin: 'wǒ míng tiān zài jiā gōng zuò',
        english: 'I’m working from home tomorrow.',
      },
      {
        text: '[他](Subject)[每天](Time)[跟朋友](Manner)[在公园](Place)[跑步](Verb)。',
        pinyin: 'tā měi tiān gēn péng you zài gōng yuán pǎo bù',
        english: 'He runs in the park with a friend every day.',
      },
      {
        text: '[我们](Subject)[晚上七点](Time)[在饭馆](Place)[吃](Verb)[饭](Object)。',
        pinyin: 'wǒ men wǎn shang qī diǎn zài fàn guǎn chī fàn',
        english: 'We’re having dinner at the restaurant at seven tonight.',
      },
    ],
    pitfall: {
      wrong: '我吃饭在饭馆。',
      wrongPinyin: 'wǒ chī fàn zài fàn guǎn',
      right: '我在饭馆吃饭。',
      rightPinyin: 'wǒ zài fàn guǎn chī fàn',
      why: 'English says what you did, then where. Chinese puts 在 and the place before the verb.',
    },
    note: 'Time can also open the sentence — 明天我在家工作 is just as good. And a few verbs put 在 after themselves to say where something ends up: 住在北京, 放在桌子上.',
  },
  {
    id: 'zh-adjectives-hen',
    level: 1,
    topic: 'verbs',
    title: 'Adjectives don’t take 是',
    rule: 'An adjective works like a verb, so “I am busy” has no 是. On its own it sounds like a comparison, so 很 goes in front — usually without meaning much “very” at all.',
    pattern: ['Subject', { word: '很' }, 'Adjective'],
    examples: [
      { text: '[我](Subject)[很](很)[忙](Adjective)。', pinyin: 'wǒ hěn máng', english: 'I’m busy.' },
      { text: '[今天](Subject)[很](很)[冷](Adjective)。', pinyin: 'jīn tiān hěn lěng', english: 'It’s cold today.' },
      {
        text: '[这个菜](Subject)[很](很)[好吃](Adjective)。',
        pinyin: 'zhè ge cài hěn hǎo chī',
        english: 'This dish is delicious.',
      },
    ],
    pitfall: {
      wrong: '我是忙。',
      wrongPinyin: 'wǒ shì máng',
      right: '我很忙。',
      rightPinyin: 'wǒ hěn máng',
      why: '是 joins two nouns — 我是老师 — not a noun and an adjective.',
    },
    note: 'Leave 很 out and the adjective implies a contrast: 今天冷 suggests today is cold, as opposed to some other day.',
  },
  {
    id: 'zh-ma-questions',
    level: 1,
    topic: 'questions',
    title: 'Add 吗 to ask yes or no',
    rule: 'Any statement becomes a yes-or-no question when 吗 goes on the end. Nothing else moves.',
    pattern: ['Statement', { word: '吗' }],
    examples: [
      { text: '[你是老师](Statement)[吗](吗)？', pinyin: 'nǐ shì lǎo shī ma', english: 'Are you a teacher?' },
      { text: '[你喜欢咖啡](Statement)[吗](吗)？', pinyin: 'nǐ xǐ huan kā fēi ma', english: 'Do you like coffee?' },
      { text: '[他明天来](Statement)[吗](吗)？', pinyin: 'tā míng tiān lái ma', english: 'Is he coming tomorrow?' },
    ],
    note: 'To answer, repeat the verb rather than saying “yes”: 是 or 不是, 喜欢 or 不喜欢. Verb–不–verb asks the same thing: 你去不去？',
  },
  {
    id: 'zh-question-words',
    level: 1,
    topic: 'questions',
    title: 'Question words go where the answer goes',
    rule: 'English moves “what”, “where” and “who” to the front. Chinese leaves the question word exactly where the answer will sit, so a question and its answer have the same shape.',
    pattern: ['Subject', 'Verb', 'Question word'],
    examples: [
      { text: '[你](Subject)[去](Verb)[哪儿](Question word)？', pinyin: 'nǐ qù nǎr', english: 'Where are you going?' },
      {
        text: '[你](Subject)[叫](Verb)[什么](Question word)名字？',
        pinyin: 'nǐ jiào shén me míng zi',
        english: 'What’s your name?',
      },
      {
        text: '[这](Subject)[是](Verb)[谁](Question word)的手机？',
        pinyin: 'zhè shì shéi de shǒu jī',
        english: 'Whose phone is this?',
      },
    ],
    pitfall: {
      wrong: '哪儿你去？',
      wrongPinyin: 'nǎr nǐ qù',
      right: '你去哪儿？',
      rightPinyin: 'nǐ qù nǎr',
      why: 'Moving the question word to the front, as English does, doesn’t work. Question and answer share an order: 你去哪儿？ — 我去北京。',
    },
    note: 'Don’t add 吗 as well. 你去哪儿吗？ is a different question: “are you going anywhere?”',
  },
  {
    id: 'zh-measure-words',
    level: 1,
    topic: 'nouns',
    title: 'A number needs a measure word',
    rule: 'A number can’t go straight onto a noun. A measure word sits between them — 个 for a great many things, and a more specific one for many common nouns.',
    pattern: ['Number', 'Measure word', 'Noun'],
    examples: [
      {
        text: '我有[两](Number)[个](Measure word)[孩子](Noun)。',
        pinyin: 'wǒ yǒu liǎng ge hái zi',
        english: 'I have two children.',
      },
      {
        text: '我要[一](Number)[杯](Measure word)[咖啡](Noun)。',
        pinyin: 'wǒ yào yì bēi kā fēi',
        english: 'I’d like a coffee.',
      },
      {
        text: '他买了[三](Number)[本](Measure word)[书](Noun)。',
        pinyin: 'tā mǎi le sān běn shū',
        english: 'He bought three books.',
      },
    ],
    pitfall: {
      wrong: '我有二个孩子。',
      wrongPinyin: 'wǒ yǒu èr ge hái zi',
      right: '我有两个孩子。',
      rightPinyin: 'wǒ yǒu liǎng ge hái zi',
      why: 'Before a measure word, “two” is 两. 二 is for counting aloud and inside bigger numbers, like 十二.',
    },
    note: '这 and 那 need one too: 这个人, 那本书.',
  },
  {
    id: 'zh-bu-mei',
    level: 1,
    topic: 'negation',
    title: '不 for habits and plans, 没 for what didn’t happen',
    rule: '不 negates habits, wishes and the future. 没 negates something that did not happen — and it is the only way to negate 有.',
    pattern: [{ word: ['不', '没'] }, 'Verb'],
    examples: [
      { text: '我[不](不)[喝](Verb)咖啡。', pinyin: 'wǒ bù hē kā fēi', english: 'I don’t drink coffee.' },
      { text: '他明天[不](不)[来](Verb)。', pinyin: 'tā míng tiān bù lái', english: 'He isn’t coming tomorrow.' },
      { text: '我昨天[没](没)[去](Verb)。', pinyin: 'wǒ zuó tiān méi qù', english: 'I didn’t go yesterday.' },
      { text: '我[没](没)[有](Verb)车。', pinyin: 'wǒ méi yǒu chē', english: 'I don’t have a car.' },
    ],
    pitfall: {
      wrong: '我不有车。',
      wrongPinyin: 'wǒ bù yǒu chē',
      right: '我没有车。',
      rightPinyin: 'wǒ méi yǒu chē',
      why: '有 is only ever negated with 没. 不有 is never right.',
    },
  },

  // ------------------------------------------------------------------ HSK 2
  {
    id: 'zh-de-modifiers',
    level: 2,
    topic: 'nouns',
    title: 'Describers go before the noun, joined by 的',
    rule: 'Whatever describes a noun goes in front of it — an owner, an adjective, even a whole clause — and 的 joins it on. The noun always comes last.',
    pattern: ['Describer', { word: '的' }, 'Noun'],
    examples: [
      {
        text: '这是[我](Describer)[的](的)[手机](Noun)。',
        pinyin: 'zhè shì wǒ de shǒu jī',
        english: 'This is my phone.',
      },
      {
        text: '她是一个[很好](Describer)[的](的)[老师](Noun)。',
        pinyin: 'tā shì yí ge hěn hǎo de lǎo shī',
        english: 'She’s a very good teacher.',
      },
      {
        text: '这是[我昨天买](Describer)[的](的)[书](Noun)。',
        pinyin: 'zhè shì wǒ zuó tiān mǎi de shū',
        english: 'This is the book I bought yesterday.',
      },
    ],
    pitfall: {
      wrong: '这是书我昨天买的。',
      wrongPinyin: 'zhè shì shū wǒ zuó tiān mǎi de',
      right: '这是我昨天买的书。',
      rightPinyin: 'zhè shì wǒ zuó tiān mǎi de shū',
      why: 'English puts “that I bought” after the noun. Chinese puts the whole clause in front, and the noun comes last.',
    },
    note: 'With family and your own groups, 的 is often dropped: 我妈妈, 我们公司.',
  },
  {
    id: 'zh-le-verb',
    level: 2,
    topic: 'particles',
    title: '了 after a verb: it’s done',
    rule: 'Put 了 straight after a verb to say the action is complete. It is not a past tense — many past sentences have no 了 — it marks something finished.',
    pattern: ['Verb', { word: '了' }, 'Object'],
    examples: [
      {
        text: '我[买](Verb)[了](了)[两张票](Object)。',
        pinyin: 'wǒ mǎi le liǎng zhāng piào',
        english: 'I bought two tickets.',
      },
      {
        text: '他[喝](Verb)[了](了)[一杯咖啡](Object)。',
        pinyin: 'tā hē le yì bēi kā fēi',
        english: 'He drank a cup of coffee.',
      },
      {
        text: '我们[看](Verb)[了](了)[一个电影](Object)。',
        pinyin: 'wǒ men kàn le yí ge diàn yǐng',
        english: 'We watched a movie.',
      },
    ],
    pitfall: {
      wrong: '我没去了。',
      wrongPinyin: 'wǒ méi qù le',
      right: '我没去。',
      rightPinyin: 'wǒ méi qù',
      why: 'A finished action is negated with 没, and 没 replaces 了 — the two don’t go together.',
    },
    note: 'With a bare object, 我吃了饭 sounds unfinished — it wants a number, a detail, or a second clause. Past states need no 了 at all: 我昨天很忙.',
  },
  {
    id: 'zh-le-change',
    level: 2,
    topic: 'particles',
    title: '了 at the end: something has changed',
    rule: 'At the end of a sentence, 了 announces a new situation: something is true now that wasn’t before.',
    pattern: ['Situation', { word: '了' }],
    examples: [
      { text: '[下雨](Situation)[了](了)。', pinyin: 'xià yǔ le', english: 'It’s started raining.' },
      { text: '[我饿](Situation)[了](了)。', pinyin: 'wǒ è le', english: 'I’m hungry now.' },
      { text: '[我不去](Situation)[了](了)。', pinyin: 'wǒ bú qù le', english: 'I’m not going after all.' },
      { text: '[他三十岁](Situation)[了](了)。', pinyin: 'tā sān shí suì le', english: 'He’s thirty now.' },
    ],
    note: 'The two kinds of 了 can share a sentence: 我学了两年中文了 means you have studied for two years and are still at it.',
  },
  {
    id: 'zh-guo',
    level: 2,
    topic: 'particles',
    title: '过 after a verb: you’ve done it before',
    rule: '过 says something has happened at least once, without saying when. It is the “have you ever” of Chinese.',
    pattern: ['Verb', { word: '过' }, 'Object'],
    examples: [
      { text: '我[去](Verb)[过](过)[中国](Object)。', pinyin: 'wǒ qù guo zhōng guó', english: 'I’ve been to China.' },
      {
        text: '你[吃](Verb)[过](过)[北京烤鸭](Object)吗？',
        pinyin: 'nǐ chī guo běi jīng kǎo yā ma',
        english: 'Have you ever had Peking duck?',
      },
      {
        text: '我没[看](Verb)[过](过)[这个电影](Object)。',
        pinyin: 'wǒ méi kàn guo zhè ge diàn yǐng',
        english: 'I’ve never seen this movie.',
      },
    ],
    note: 'Unlike 了, 过 survives negation: 我没去过 — I’ve never been.',
  },
  {
    id: 'zh-bi',
    level: 2,
    topic: 'order',
    title: 'Compare with 比, and leave out 很',
    rule: 'A 比 B plus an adjective says A is more so than B. The adjective stays bare; any “how much” goes after it.',
    pattern: ['A', { word: '比' }, 'B', 'Adjective'],
    examples: [
      {
        text: '[今天](A)[比](比)[昨天](B)[冷](Adjective)。',
        pinyin: 'jīn tiān bǐ zuó tiān lěng',
        english: 'Today is colder than yesterday.',
      },
      {
        text: '[他](A)[比](比)[我](B)[高](Adjective)一点儿。',
        pinyin: 'tā bǐ wǒ gāo yì diǎnr',
        english: 'He’s a little taller than me.',
      },
      {
        text: '[坐地铁](A)[比](比)[打车](B)[快](Adjective)多了。',
        pinyin: 'zuò dì tiě bǐ dǎ chē kuài duō le',
        english: 'The subway is much faster than a taxi.',
      },
    ],
    pitfall: {
      wrong: '他比我很高。',
      wrongPinyin: 'tā bǐ wǒ hěn gāo',
      right: '他比我高很多。',
      rightPinyin: 'tā bǐ wǒ gāo hěn duō',
      why: '很 can’t go inside a 比 comparison. Say how much after the adjective instead: 高很多, 高一点儿.',
    },
    note: 'For “not as … as”, use 没有: 我没有他高.',
  },
  {
    id: 'zh-duration',
    level: 2,
    topic: 'order',
    title: 'When goes before the verb; how long goes after',
    rule: 'A point in time — 明天, 三点 — goes before the verb. A length of time — 两年, 半个小时 — goes after it.',
    pattern: ['Subject', 'Verb', 'Duration'],
    examples: [
      {
        text: '[我](Subject)[等](Verb)了[半个小时](Duration)。',
        pinyin: 'wǒ děng le bàn ge xiǎo shí',
        english: 'I waited half an hour.',
      },
      {
        text: '[她](Subject)在北京[住](Verb)了[三年](Duration)。',
        pinyin: 'tā zài běi jīng zhù le sān nián',
        english: 'She lived in Beijing for three years.',
      },
      {
        text: '[我](Subject)每天[睡](Verb)[七个小时](Duration)。',
        pinyin: 'wǒ měi tiān shuì qī ge xiǎo shí',
        english: 'I sleep seven hours a night.',
      },
    ],
    pitfall: {
      wrong: '我两年学中文。',
      wrongPinyin: 'wǒ liǎng nián xué zhōng wén',
      right: '我学了两年中文。',
      rightPinyin: 'wǒ xué le liǎng nián zhōng wén',
      why: 'Two years is how long, so it follows the verb — and with an object, it sits between the verb and the object.',
    },
    note: '我每天睡七个小时 has both at once: 每天 is when, before the verb; 七个小时 is how long, after it.',
  },
  {
    id: 'zh-de-degree',
    level: 2,
    topic: 'verbs',
    title: '得 after a verb: how well it’s done',
    rule: 'To say how someone does something, put 得 after the verb and the description after 得.',
    pattern: ['Verb', { word: '得' }, 'How'],
    examples: [
      { text: '他[跑](Verb)[得](得)[很快](How)。', pinyin: 'tā pǎo de hěn kuài', english: 'He runs fast.' },
      { text: '你[说](Verb)[得](得)[对](How)。', pinyin: 'nǐ shuō de duì', english: 'You’re right.' },
      {
        text: '她中文[说](Verb)[得](得)[很好](How)。',
        pinyin: 'tā zhōng wén shuō de hěn hǎo',
        english: 'She speaks Chinese very well.',
      },
    ],
    pitfall: {
      wrong: '她说中文得很好。',
      wrongPinyin: 'tā shuō zhōng wén de hěn hǎo',
      right: '她中文说得很好。',
      rightPinyin: 'tā zhōng wén shuō de hěn hǎo',
      why: 'Nothing can come between the verb and 得. Move the object in front — or say the verb twice: 她说中文说得很好.',
    },
  },
  {
    id: 'zh-hui-neng-keyi',
    level: 2,
    topic: 'verbs',
    title: '会, 能 and 可以: three kinds of “can”',
    rule: '会 is a skill you learned. 能 is being able to, given the circumstances. 可以 is being allowed. All three go straight before the verb.',
    pattern: [{ word: ['会', '能', '可以'] }, 'Verb'],
    shape: 'sequence',
    examples: [
      { text: '我[会](会)[游泳](Verb)。', pinyin: 'wǒ huì yóu yǒng', english: 'I can swim.' },
      { text: '我今天不[能](能)[去](Verb)。', pinyin: 'wǒ jīn tiān bù néng qù', english: 'I can’t go today.' },
      {
        text: '这里[可以](可以)[拍照](Verb)吗？',
        pinyin: 'zhè lǐ kě yǐ pāi zhào ma',
        english: 'Can I take photos here?',
      },
    ],
    note: 'A language is a skill, so it takes 会: 我会说一点儿中文. 会 also means “will probably”: 明天会下雨.',
  },
  {
    id: 'zh-zai-progressive',
    level: 2,
    topic: 'verbs',
    title: '在 before a verb: happening right now',
    rule: 'Put 在, or 正在, in front of a verb for something in progress. 呢 often comes along at the end.',
    pattern: [{ word: ['在', '正在'] }, 'Verb', { word: '呢' }],
    examples: [
      { text: '我[在](在)[吃](Verb)饭[呢](呢)。', pinyin: 'wǒ zài chī fàn ne', english: 'I’m eating.' },
      { text: '他[正在](正在)[开](Verb)会。', pinyin: 'tā zhèng zài kāi huì', english: 'He’s in a meeting.' },
      { text: '你[在](在)[做](Verb)什么？', pinyin: 'nǐ zài zuò shén me', english: 'What are you doing?' },
    ],
    note: 'This is the same 在 as in 我在家 — one word doing two jobs.',
  },
  {
    id: 'zh-reduplication',
    level: 2,
    topic: 'verbs',
    title: 'Say a verb twice to make it light',
    rule: 'Doubling a verb softens it to “have a quick …” or “just …”. It makes a request sound easy and a suggestion sound relaxed.',
    pattern: ['Verb', 'Verb again'],
    examples: [
      { text: '我[看](Verb)[看](Verb again)。', pinyin: 'wǒ kàn kan', english: 'Let me have a look.' },
      { text: '你[试](Verb)[试](Verb again)这个。', pinyin: 'nǐ shì shi zhè ge', english: 'Try this one.' },
      {
        text: '我们[休息](Verb)[休息](Verb again)吧。',
        pinyin: 'wǒ men xiū xi xiū xi ba',
        english: 'Let’s take a little break.',
      },
    ],
    note: 'A two-syllable verb doubles as a whole word: 休息休息, never 休休息息.',
  },

  // ------------------------------------------------------------------ HSK 3
  {
    id: 'zh-ba',
    level: 3,
    topic: 'order',
    title: '把: bring the object forward and do something to it',
    rule: '把 moves the object in front of the verb so the sentence can say what became of it. The verb can’t stand alone — it needs a result, a direction or a place after it.',
    pattern: ['Subject', { word: '把' }, 'Object', 'Verb', 'Result'],
    examples: [
      {
        text: '[我](Subject)[把](把)[手机](Object)[忘](Verb)[在家里](Result)了。',
        pinyin: 'wǒ bǎ shǒu jī wàng zài jiā li le',
        english: 'I left my phone at home.',
      },
      {
        text: '请[把](把)[门](Object)[关](Verb)[上](Result)。',
        pinyin: 'qǐng bǎ mén guān shang',
        english: 'Please close the door.',
      },
      {
        text: '[他](Subject)[把](把)[作业](Object)[做](Verb)[完](Result)了。',
        pinyin: 'tā bǎ zuò yè zuò wán le',
        english: 'He finished his homework.',
      },
    ],
    pitfall: {
      wrong: '我把书看。',
      wrongPinyin: 'wǒ bǎ shū kàn',
      right: '我把书看完了。',
      rightPinyin: 'wǒ bǎ shū kàn wán le',
      why: 'A bare verb after 把 is left hanging. Say what became of the book: 看完了, finished.',
    },
    note: 'The object has to be something specific that you both know about — this book, the door — not just “a book”.',
  },
  {
    id: 'zh-resultative',
    level: 3,
    topic: 'verbs',
    title: 'Verb plus result: did it work?',
    rule: 'Chinese separates trying from succeeding. 看 is to look and 看见 is to see; a result word after the verb says the action got somewhere.',
    pattern: ['Verb', 'Result'],
    examples: [
      { text: '我[听](Verb)[懂](Result)了。', pinyin: 'wǒ tīng dǒng le', english: 'I understood.' },
      {
        text: '你[找](Verb)[到](Result)钥匙了吗？',
        pinyin: 'nǐ zhǎo dào yào shi le ma',
        english: 'Did you find the keys?',
      },
      { text: '我没[看](Verb)[见](Result)他。', pinyin: 'wǒ méi kàn jiàn tā', english: 'I didn’t see him.' },
    ],
    pitfall: {
      wrong: '我不听懂。',
      wrongPinyin: 'wǒ bù tīng dǒng',
      right: '我没听懂。',
      rightPinyin: 'wǒ méi tīng dǒng',
      why: 'A result either happened or it didn’t, so it is negated with 没.',
    },
    note: '找 is to look for; 找到 is to find. 我找了半天，没找到 — I looked for ages and didn’t find it.',
  },
  {
    id: 'zh-shi-de',
    level: 3,
    topic: 'order',
    title: '是…的: when, where or how it happened',
    rule: 'When everyone already knows something happened, 是…的 puts the spotlight on a detail — when, where, how, or who with.',
    pattern: [{ word: '是' }, 'Detail', 'Verb', { word: '的' }],
    examples: [
      {
        text: '你[是](是)[什么时候](Detail)[来](Verb)[的](的)？',
        pinyin: 'nǐ shì shén me shí hou lái de',
        english: 'When did you get here?',
      },
      {
        text: '我[是](是)[坐飞机](Detail)[来](Verb)[的](的)。',
        pinyin: 'wǒ shì zuò fēi jī lái de',
        english: 'I came by plane.',
      },
      {
        text: '我们[是](是)[在上海](Detail)[认识](Verb)[的](的)。',
        pinyin: 'wǒ men shì zài shàng hǎi rèn shi de',
        english: 'We met in Shanghai.',
      },
    ],
    pitfall: {
      wrong: '我是坐飞机来了。',
      wrongPinyin: 'wǒ shì zuò fēi jī lái le',
      right: '我是坐飞机来的。',
      rightPinyin: 'wǒ shì zuò fēi jī lái de',
      why: 'The frame closes with 的, not 了. That you came is old news; how you came is the point.',
    },
    note: 'In speech 是 is often dropped — 我坐飞机来的 — but the 的 stays.',
  },
  {
    id: 'zh-paired-connectors',
    level: 3,
    topic: 'linking',
    title: 'Linking words come in pairs',
    rule: 'Where English uses one linking word, Chinese often puts one in each half. “Although it’s pricey, it’s good” takes both 虽然 and 但是.',
    pattern: [{ word: ['虽然', '因为'] }, 'First half', { word: ['但是', '所以'] }, 'Second half'],
    examples: [
      {
        text: '[虽然](虽然)[有点儿贵](First half)，[但是](但是)[很好吃](Second half)。',
        pinyin: 'suī rán yǒu diǎnr guì dàn shì hěn hǎo chī',
        english: 'It’s a bit pricey, but it’s delicious.',
      },
      {
        text: '[因为](因为)[下雨](First half)，[所以](所以)[我们没去](Second half)。',
        pinyin: 'yīn wèi xià yǔ suǒ yǐ wǒ men méi qù',
        english: 'It was raining, so we didn’t go.',
      },
      {
        text: '[虽然](虽然)[他很忙](First half)，[但是](但是)[每天都学中文](Second half)。',
        pinyin: 'suī rán tā hěn máng dàn shì měi tiān dōu xué zhōng wén',
        english: 'Busy as he is, he studies Chinese every day.',
      },
    ],
    note: 'In speech either half can drop out — 因为下雨，我们没去 — but 虽然 almost always brings 但是 or 可是 with it.',
  },
  {
    id: 'zh-directional',
    level: 3,
    topic: 'verbs',
    title: '来 toward the speaker, 去 away',
    rule: 'After a verb of movement, 来 means toward the speaker and 去 means away. A direction word in between — 进, 出, 上, 下, 回, 过 — gives the full path.',
    pattern: ['Verb', 'Direction', { word: ['来', '去'] }],
    examples: [
      { text: '他[走](Verb)[出](Direction)[去](去)了。', pinyin: 'tā zǒu chū qu le', english: 'He walked out.' },
      {
        text: '你把书[拿](Verb)[过](Direction)[来](来)。',
        pinyin: 'nǐ bǎ shū ná guò lai',
        english: 'Bring the book over here.',
      },
      { text: '请[进](Direction)[来](来)。', pinyin: 'qǐng jìn lai', english: 'Come in, please.' },
      { text: '我明天[回](Direction)[去](去)。', pinyin: 'wǒ míng tiān huí qu', english: 'I’m going back tomorrow.' },
    ],
    note: 'Which one depends on where the speaker is. Someone inside says 进来; you, outside, say 我进去.',
  },
  {
    id: 'zh-bei',
    level: 3,
    topic: 'order',
    title: '被: something was done to it',
    rule: '被 puts the thing affected first and the doer after 被. In everyday speech it is used mostly for things that went wrong.',
    pattern: ['Affected', { word: '被' }, 'Doer', 'Verb'],
    examples: [
      {
        text: '[他](Affected)[被](被)[老板](Doer)[批评](Verb)了。',
        pinyin: 'tā bèi lǎo bǎn pī píng le',
        english: 'He got chewed out by his boss.',
      },
      {
        text: '[我的自行车](Affected)[被](被)[偷](Verb)了。',
        pinyin: 'wǒ de zì xíng chē bèi tōu le',
        english: 'My bike got stolen.',
      },
      {
        text: '[蛋糕](Affected)[被](被)[孩子们](Doer)[吃](Verb)完了。',
        pinyin: 'dàn gāo bèi hái zi men chī wán le',
        english: 'The kids ate all the cake.',
      },
    ],
    note: 'The doer can be left out when it doesn’t matter, as with the bike. Like 把, the verb needs something after it: 了, a result, a direction.',
  },

  // ------------------------------------------------------------------ HSK 4
  {
    id: 'zh-potential',
    level: 4,
    topic: 'verbs',
    title: '得 or 不 inside a verb: can it be done?',
    rule: 'Put 得 or 不 between a verb and its result to say whether the result is possible. This, rather than 能, is how Chinese usually says “can’t understand” or “can’t finish”.',
    pattern: ['Verb', { word: ['得', '不'] }, 'Result'],
    examples: [
      {
        text: '对不起，我[听](Verb)[不](不)[懂](Result)。',
        pinyin: 'duì bu qǐ wǒ tīng bu dǒng',
        english: 'Sorry, I don’t understand.',
      },
      {
        text: '这么多菜，我们[吃](Verb)[得](得)[完](Result)吗？',
        pinyin: 'zhè me duō cài wǒ men chī de wán ma',
        english: 'That’s a lot of food — can we finish it?',
      },
      {
        text: '太晚了，[买](Verb)[不](不)[到](Result)票了。',
        pinyin: 'tài wǎn le mǎi bu dào piào le',
        english: 'It’s too late — we won’t get tickets now.',
      },
    ],
    pitfall: {
      wrong: '我不能听懂。',
      wrongPinyin: 'wǒ bù néng tīng dǒng',
      right: '我听不懂。',
      rightPinyin: 'wǒ tīng bu dǒng',
      why: '能 in front of a result sounds foreign. The “can” goes inside the verb instead.',
    },
  },
  {
    id: 'zh-lian-dou',
    level: 4,
    topic: 'order',
    title: '连…都: even',
    rule: 'To say “even”, put 连 before the surprising part and 都 or 也 before the verb.',
    pattern: [{ word: '连' }, 'Surprising part', { word: ['都', '也'] }, 'Verb'],
    examples: [
      {
        text: '我[连](连)[他的名字](Surprising part)[都](都)[忘](Verb)了。',
        pinyin: 'wǒ lián tā de míng zi dōu wàng le',
        english: 'I’ve even forgotten his name.',
      },
      {
        text: '他[连](连)[一句中文](Surprising part)[都](都)不会[说](Verb)。',
        pinyin: 'tā lián yí jù zhōng wén dōu bú huì shuō',
        english: 'He can’t say a single sentence of Chinese.',
      },
      {
        text: '这个问题[连](连)[老师](Surprising part)[也](也)不[知道](Verb)。',
        pinyin: 'zhè ge wèn tí lián lǎo shī yě bù zhī dao',
        english: 'Even the teacher doesn’t know the answer to this one.',
      },
    ],
  },
  {
    id: 'zh-topic-comment',
    level: 4,
    topic: 'order',
    title: 'Topic first, then what you have to say about it',
    rule: 'Chinese often opens with the thing under discussion and only then makes a sentence about it — even when that means the object comes first.',
    pattern: ['Topic', 'Comment'],
    examples: [
      { text: '[这本书](Topic)[我看过](Comment)。', pinyin: 'zhè běn shū wǒ kàn guo', english: 'I’ve read this book.' },
      {
        text: '[中文](Topic)，[我觉得不太难](Comment)。',
        pinyin: 'zhōng wén wǒ jué de bú tài nán',
        english: 'Chinese — I don’t find it too hard.',
      },
      {
        text: '[那家饭馆](Topic)，[菜很好吃，就是有点儿贵](Comment)。',
        pinyin: 'nà jiā fàn guǎn cài hěn hǎo chī jiù shì yǒu diǎnr guì',
        english: 'That restaurant — the food’s great, it’s just a bit expensive.',
      },
    ],
    note: 'This is why a Chinese sentence can seem to start with its object. Nothing has moved; the topic is simply said first.',
  },
  {
    id: 'zh-zhiyao-zhiyou',
    level: 4,
    topic: 'linking',
    title: '只要…就 and 只有…才: enough, or the only way',
    rule: '只要…就 says one condition is enough. 只有…才 says it is the only way. In English, “as long as” and “only if”.',
    pattern: [{ word: ['只要', '只有'] }, 'Condition', { word: ['就', '才'] }, 'Result'],
    examples: [
      {
        text: '[只要](只要)[你每天练习](Condition)，[就](就)[能说好](Result)。',
        pinyin: 'zhǐ yào nǐ měi tiān liàn xí jiù néng shuō hǎo',
        english: 'As long as you practice every day, you’ll speak it well.',
      },
      {
        text: '[只有](只有)[多说](Condition)，[才](才)[能说得流利](Result)。',
        pinyin: 'zhǐ yǒu duō shuō cái néng shuō de liú lì',
        english: 'Only by speaking a lot will you become fluent.',
      },
    ],
    pitfall: {
      wrong: '只有多说，就能说得流利。',
      wrongPinyin: 'zhǐ yǒu duō shuō jiù néng shuō de liú lì',
      right: '只有多说，才能说得流利。',
      rightPinyin: 'zhǐ yǒu duō shuō cái néng shuō de liú lì',
      why: 'The pairs don’t mix: 只要 goes with 就, and 只有 with 才.',
    },
    note: '就 and 才 sit before the verb, after the second subject if there is one: 只要你来，我就高兴.',
  },
  {
    id: 'zh-final-particles',
    level: 4,
    topic: 'particles',
    title: '吧, 呢, 啊 and 嘛 set the tone of voice',
    rule: 'The last syllable of a sentence often carries its attitude. 吧 softens it into a suggestion or a guess; 呢 turns a question back or keeps it open; 啊 adds warmth; 嘛 says “obviously”.',
    pattern: ['Sentence', { word: ['吧', '呢', '啊', '嘛'] }],
    examples: [
      { text: '[我们走](Sentence)[吧](吧)。', pinyin: 'wǒ men zǒu ba', english: 'Let’s go.' },
      { text: '[我很好，你](Sentence)[呢](呢)？', pinyin: 'wǒ hěn hǎo nǐ ne', english: 'I’m fine — and you?' },
      { text: '[好](Sentence)[啊](啊)！', pinyin: 'hǎo a', english: 'Sure!' },
      { text: '[这个很简单](Sentence)[嘛](嘛)。', pinyin: 'zhè ge hěn jiǎn dān ma', english: 'Come on, this is easy.' },
    ],
    note: '吧 on a statement turns it into a guess you want confirmed: 你是美国人吧？',
  },

  // ------------------------------------------------------------------ HSK 5
  {
    id: 'zh-formal-words',
    level: 5,
    topic: 'register',
    title: 'Formal Chinese has its own small words',
    rule: 'Written and formal Chinese trades some everyday words for short classical ones: 之 for 的, 于 for 在, 以 for 用. Recognizing them matters more than producing them.',
    pattern: [{ word: ['之', '于', '以'] }],
    shape: 'contrast',
    examples: [
      {
        text: '这是我们成功的原因[之](之)一。',
        pinyin: 'zhè shì wǒ men chéng gōng de yuán yīn zhī yī',
        english: 'This is one of the reasons we succeeded.',
      },
      {
        text: '这家公司成立[于](于)一九九八年。',
        pinyin: 'zhè jiā gōng sī chéng lì yú yī jiǔ jiǔ bā nián',
        english: 'The company was founded in 1998.',
      },
      {
        text: '我们[以](以)客户为中心。',
        pinyin: 'wǒ men yǐ kè hù wéi zhōng xīn',
        english: 'We put our customers first.',
      },
    ],
    note: '以…为… — “take A as B” — turns up constantly in mission statements and the news: 以人为本, “people first”.',
  },
  {
    id: 'zh-zhisuoyi',
    level: 5,
    topic: 'linking',
    title: '之所以…是因为: result first, reason second',
    rule: 'To state a result first and then give the reason, formally, open with 之所以 and bring in the reason with 是因为.',
    pattern: ['Subject', { word: '之所以' }, 'Result', { word: '是因为' }, 'Reason'],
    examples: [
      {
        text: '[我](Subject)[之所以](之所以)[学中文](Result)，[是因为](是因为)[我喜欢中国历史](Reason)。',
        pinyin: 'wǒ zhī suǒ yǐ xué zhōng wén shì yīn wèi wǒ xǐ huan zhōng guó lì shǐ',
        english: 'The reason I study Chinese is that I love Chinese history.',
      },
      {
        text: '[他](Subject)[之所以](之所以)[迟到](Result)，[是因为](是因为)[路上堵车](Reason)。',
        pinyin: 'tā zhī suǒ yǐ chí dào shì yīn wèi lù shang dǔ chē',
        english: 'The reason he was late was the traffic.',
      },
    ],
    note: 'Everyday order puts the reason first: 因为路上堵车，所以他迟到了.',
  },
  {
    id: 'zh-bushi-ershi',
    level: 5,
    topic: 'linking',
    title: '不是…而是: not this, but that',
    rule: 'To correct an idea, put the wrong one after 不是 and the right one after 而是. It sounds considered, and turns up constantly in explanations.',
    pattern: [{ word: '不是' }, 'Not this', { word: '而是' }, 'But this'],
    examples: [
      {
        text: '问题[不是](不是)[钱](Not this)，[而是](而是)[时间](But this)。',
        pinyin: 'wèn tí bú shì qián ér shì shí jiān',
        english: 'The problem isn’t money, it’s time.',
      },
      {
        text: '[不是](不是)[我不想去](Not this)，[而是](而是)[真的没有时间](But this)。',
        pinyin: 'bú shì wǒ bù xiǎng qù ér shì zhēn de méi yǒu shí jiān',
        english: 'It’s not that I don’t want to go — I really don’t have time.',
      },
    ],
  },

  // ------------------------------------------------------------------ HSK 6
  {
    id: 'zh-yuqi-buru',
    level: 6,
    topic: 'linking',
    title: '与其…不如: rather than this, better that',
    rule: '与其 introduces the option you are turning down; 不如 introduces the better one.',
    pattern: [{ word: '与其' }, 'Turned down', { word: '不如' }, 'Better'],
    examples: [
      {
        text: '[与其](与其)[在家等](Turned down)，[不如](不如)[去找他](Better)。',
        pinyin: 'yǔ qí zài jiā děng bù rú qù zhǎo tā',
        english: 'Rather than wait at home, we’d do better to go and find him.',
      },
      {
        text: '[与其](与其)[抱怨](Turned down)，[不如](不如)[想办法解决](Better)。',
        pinyin: 'yǔ qí bào yuàn bù rú xiǎng bàn fǎ jiě jué',
        english: 'Instead of complaining, find a way to fix it.',
      },
    ],
  },
  {
    id: 'zh-hekuang',
    level: 6,
    topic: 'linking',
    title: '何况: let alone',
    rule: '何况 adds a stronger case to the end of an argument — “let alone”, “never mind”. The first half usually has 都 or 也.',
    pattern: ['First case', { word: ['都', '也'] }, 'Claim', { word: '何况' }, 'Stronger case'],
    examples: [
      {
        text: '这个问题连[老师](First case)[都](都)[不会](Claim)，[何况](何况)[我](Stronger case)呢？',
        pinyin: 'zhè ge wèn tí lián lǎo shī dōu bú huì hé kuàng wǒ ne',
        english: 'Even the teacher can’t do this one, let alone me.',
      },
      {
        text: '他[中文](First case)[都](都)[说不好](Claim)，[何况](何况)[英文](Stronger case)呢？',
        pinyin: 'tā zhōng wén dōu shuō bu hǎo hé kuàng yīng wén ne',
        english: 'He can’t even speak Chinese well, let alone English.',
      },
    ],
    note: 'In everyday speech 更别说 does the same job: 我连饭都没吃，更别说休息了.',
  },
  {
    id: 'zh-suo',
    level: 6,
    topic: 'register',
    title: '所 before a verb: the formal “what”',
    rule: 'In formal Chinese, 所 goes before the verb of a describing clause: 我所知道的 is “what I know”. It adds weight, not meaning.',
    pattern: ['Subject', { word: '所' }, 'Verb', { word: '的' }],
    examples: [
      {
        text: '[我](Subject)[所](所)[知道](Verb)[的](的)就是这些。',
        pinyin: 'wǒ suǒ zhī dao de jiù shì zhè xiē',
        english: 'This is all I know.',
      },
      {
        text: '[大家](Subject)[所](所)[关心](Verb)[的](的)问题是价格。',
        pinyin: 'dà jiā suǒ guān xīn de wèn tí shì jià gé',
        english: 'The issue everyone cares about is price.',
      },
    ],
    note: 'Drop 所 and the meaning is unchanged — 我知道的就是这些 — only less formal.',
  },
]
